import type { Participant, Stream, VideoPlayer } from "@zoom/videosdk";
import type { RemoteVideoDelegator } from "../../../../core/room/participant/media/MediaDelegator";
import {
  MediaSource,
  QualityMode,
} from "../../../../core/room/participant/media/types";
import { handleError } from "../../../utils/handleError";

/**
 * Extracts video concerns from Zoom's monolithic Participant into granular objects
 * that our system expects. Unlike Twilio's separate track objects, Zoom bundles
 * audio/video concerns in a single participant.
 *
 * This delegator isolates the video functionality to maintain existing functionality.
 */
export class ZoomRemoteVideoDelegator implements RemoteVideoDelegator {
  readonly kind = "video" as const;
  #participant: Participant;
  #qualityMode: QualityMode | undefined;
  #zoomSession: typeof Stream;

  constructor(participant: Participant, zoomSession: typeof Stream) {
    this.#participant = participant;
    this.#zoomSession = zoomSession;
    // derive a qualityMode value for Zoom video
  }

  get sid() {
    return `${this.#participant.userId}:${this.kind}:${this.source}`;
  }

  get source() {
    return MediaSource.Camera;
  }

  get isEnabled() {
    return this.#participant.bVideoOn ?? false;
  }

  get qualityMode() {
    return this.#qualityMode;
  }

  /**
   * Attaches Zoom's WebComponents based shadow DOM <video-player> element.
   * We must provide a video quality argument, explained below:
   *
   * Video_90P = 0,
   * Video_180P = 1,
   * Video_360P = 2,
   * Video_720P = 3,
   * Video_1080P = 4
   */
  async attach(element: VideoPlayer) {
    if (this.#participant.bVideoOn) {
      try {
        await this.#zoomSession.attachVideo(
          this.#participant.userId,
          3, // video quality
          element,
        );
      } catch (attachError) {
        handleError("[ZoomRemoteVideoDelegator]: attach", attachError);
      }
    }
  }

  async detach(element: VideoPlayer) {
    try {
      await this.#zoomSession.detachVideo(this.#participant.userId, element);
    } catch (detachError) {
      handleError("[ZoomRemoteVideoDelegator]: detach", detachError);
    }
  }
}
